import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CareReportSubmissionService } from '../services/supabase/careReportSubmissionService';
import { CareReportService } from '../services/supabase/careReportService';
import './clientDetailPage.css';

const DAYS_STEP = 7;

function formatShiftRange(startStr, endStr) {
  if (!startStr) return '—';
  const start = new Date(startStr);
  const datePart = start.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!endStr) return `${datePart} ${startTime}`;
  const endTime = new Date(endStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} ${startTime} – ${endTime}`;
}

function getCaretakerName(email, caretakerList) {
  const match = caretakerList?.find(c => c.email === email);
  return match?.name ?? email ?? '—';
}

export default function ClientDetailPage({ caretakerList }) {
  const { clientName } = useParams();
  const decodedName = decodeURIComponent(clientName);

  const [allReports, setAllReports] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [visibleDays, setVisibleDays] = useState(DAYS_STEP);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingMileageId, setEditingMileageId] = useState(null);
  const [editingMileageValue, setEditingMileageValue] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      CareReportSubmissionService.getReportsForClient(decodedName),
      CareReportService.getTemplateForClient(decodedName),
    ])
      .then(([reports, template]) => {
        setAllReports(reports);
        setCustomQuestions(template?.custom_questions ?? []);
      })
      .catch(() => setError('Failed to load care reports.'))
      .finally(() => setLoading(false));
  }, [decodedName]);

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - visibleDays);
  const visibleReports = allReports.filter(r => new Date(r.shift_start) >= cutoff);
  const hasMore = allReports.some(r => new Date(r.shift_start) < cutoff);

  const saveMileage = async (reportId) => {
    const val = Number(editingMileageValue) || 0;
    try {
      await CareReportSubmissionService.updateMileage(reportId, val);
      setAllReports(prev => prev.map(r => r.id === reportId ? { ...r, mileage: val } : r));
    } catch {
      // leave editing state open so user can retry
      return;
    }
    setEditingMileageId(null);
  };

  const renderAnswer = (q, customAnswers) => {
    const val = customAnswers?.[q.id];
    if (val === undefined || val === null || val === '') return <span className="cdNoAnswer">—</span>;
    return <span>{String(val)}</span>;
  };

  return (
    <div className="clientDetailPageDiv">
      <div className="clientDetailHeader">
        <Link to="/clients" className="backToHomeLink">← Back to Clients</Link>
        <h2 className="clientDetailTitle">{decodedName}</h2>
      </div>

      <div className="formSep"></div>

      <div className="clientDetailSection">
        <h3 className="clientDetailSectionTitle">Care Reports</h3>

        {loading && <p className="cdLoading">Loading…</p>}
        {error && <p className="cdError">{error}</p>}

        {!loading && !error && visibleReports.length === 0 && (
          <p className="cdEmpty">No care reports found for the past {visibleDays} days.</p>
        )}

        {!loading && !error && visibleReports.length > 0 && (
          <div className="cdTableWrapper">
            <table className="cdTable">
              <thead>
                <tr>
                  <th className="cdTh">Shift</th>
                  <th className="cdTh">Caretaker</th>
                  <th className="cdTh">Important Information</th>
                  <th className="cdTh">Tasks</th>
                  {customQuestions.map(q => (
                    <th key={q.id} className="cdTh cdCustomTh">{q.text}</th>
                  ))}
                  <th className="cdTh">Receipts</th>
                  <th className="cdTh">Mileage</th>
                </tr>
              </thead>
              <tbody>
                {visibleReports.map(report => (
                  <tr key={report.id} className="cdRow">
                    <td className="cdTd cdDateTd">{formatShiftRange(report.shift_start, report.shift_end)}</td>
                    <td className="cdTd">{getCaretakerName(report.caretaker_email, caretakerList)}</td>
                    <td className="cdTd cdInfoTd">{report.important_information || <span className="cdNoAnswer">—</span>}</td>
                    <td className="cdTd cdTasksTd">{report.tasks || <span className="cdNoAnswer">—</span>}</td>
                    {customQuestions.map(q => (
                      <td key={q.id} className="cdTd">{renderAnswer(q, report.custom_answers)}</td>
                    ))}
                    <td className="cdTd">
                      {report.receipt_urls?.length > 0 ? (
                        <div className="cdReceiptThumbs">
                          {report.receipt_urls.map((url, i) => (
                            <a key={url} href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt={`Receipt ${i + 1}`} className="cdReceiptThumb" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="cdNoAnswer">—</span>
                      )}
                    </td>
                    <td className="cdTd cdMileageTd">
                      {editingMileageId === report.id ? (
                        <input
                          className="cdMileageInput"
                          type="number"
                          min="0"
                          step="0.1"
                          autoFocus
                          value={editingMileageValue}
                          onChange={e => setEditingMileageValue(e.target.value)}
                          onBlur={() => saveMileage(report.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveMileage(report.id);
                            if (e.key === 'Escape') setEditingMileageId(null);
                          }}
                        />
                      ) : (
                        <span
                          className="cdMileageValue"
                          onClick={() => {
                            setEditingMileageId(report.id);
                            setEditingMileageValue(report.mileage ?? 0);
                          }}
                        >
                          {report.mileage > 0 ? `${report.mileage} mi` : <span className="cdNoAnswer">—</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && hasMore && (
          <button
            className="cdShowMoreBtn"
            onClick={() => setVisibleDays(prev => prev + DAYS_STEP)}
          >
            Show More ({visibleDays + DAYS_STEP} days)
          </button>
        )}
      </div>
    </div>
  );
}
